// droppedSendDiagnostics — 描画イベントが「ソケット未 open」で WSClient に捨てられた件数を
// セッション内メモリで数え、復帰時 / pagehide に Sentry へ 1 件だけ報告する (診断のみ・挙動不変)。
//
// 背景: 再入室後にストロークが消えたという報告があり、WS が実際には死んでいる間の描画が
// `WSClient.send` の `send-before-open` で黙って捨てられていた、という仮説の検証用。
// PII 禁止: 種別名 (`stroke.segment` 等) の件数・時刻・phase・visibility のみ。表示名やチャット本文は含めない。

/** 描画系イベントか (stroke.* と whiteboard.* のクライアント送信イベント)。 */
export const isDrawingEventType = (t: string): boolean =>
  t.startsWith('stroke.') || t.startsWith('whiteboard.');

export interface DropContext {
  readonly now: number;
  /** SessionProvider の phase tag ('joined' / 'reconnecting' 等)。 */
  readonly phase: string;
  /** document.visibilityState (SSR / test では 'unknown')。 */
  readonly visibility: string;
  /** 再接続が進行中 (phase が reconnecting、または再接続タイマー待ち) だったか。 */
  readonly reconnecting: boolean;
}

export interface DroppedSendReport {
  readonly total: number;
  readonly byType: Readonly<Record<string, number>>;
  readonly firstDropAt: number;
  readonly lastDropAt: number;
  readonly byPhase: Readonly<Record<string, number>>;
  /** document.visibilityState === 'hidden' で捨てられた件数。 */
  readonly droppedWhileHidden: number;
  readonly droppedWhileReconnecting: number;
  /** どの契機で報告したか。 */
  readonly trigger: string;
}

export class DroppedSendTracker {
  private byType: Record<string, number> = {};
  private byPhase: Record<string, number> = {};
  private total = 0;
  private hidden = 0;
  private reconnecting = 0;
  private first = 0;
  private last = 0;

  /** 描画イベントでなければ無視 (false を返す)。 */
  record(eventType: string | undefined, ctx: DropContext): boolean {
    if (eventType === undefined || !isDrawingEventType(eventType)) return false;
    if (this.total === 0) this.first = ctx.now;
    this.last = ctx.now;
    this.total += 1;
    this.byType[eventType] = (this.byType[eventType] ?? 0) + 1;
    this.byPhase[ctx.phase] = (this.byPhase[ctx.phase] ?? 0) + 1;
    if (ctx.visibility === 'hidden') this.hidden += 1;
    if (ctx.reconnecting) this.reconnecting += 1;
    return true;
  }

  /** 1 件以上捨てていれば報告内容を返してカウンタをリセット。無ければ null。 */
  takeReport(trigger: string): DroppedSendReport | null {
    if (this.total === 0) return null;
    const report: DroppedSendReport = {
      total: this.total,
      byType: this.byType,
      byPhase: this.byPhase,
      firstDropAt: this.first,
      lastDropAt: this.last,
      droppedWhileHidden: this.hidden,
      droppedWhileReconnecting: this.reconnecting,
      trigger,
    };
    this.byType = {};
    this.byPhase = {};
    this.total = 0;
    this.hidden = 0;
    this.reconnecting = 0;
    this.first = 0;
    this.last = 0;
    return report;
  }
}

export const DROPPED_SEND_MESSAGE = 'drawing events dropped while socket not open';

export type CaptureWarning = (message: string, extra: Record<string, unknown>) => void;

/** report があれば 1 件だけ capture に渡す。capture が throw しても握りつぶす (診断が本体を壊さない)。 */
export const flushDroppedSends = (
  tracker: DroppedSendTracker,
  trigger: string,
  capture: CaptureWarning,
): boolean => {
  const report = tracker.takeReport(trigger);
  if (report === null) return false;
  try {
    capture(DROPPED_SEND_MESSAGE, { ...report });
  } catch {
    // best-effort
  }
  return true;
};
